import CloseIcon from "@mui/icons-material/Close";
import { LoadingButton } from "@mui/lab";
import { Box, Button, Dialog, DialogContent, DialogTitle, IconButton, TextField, Typography } from "@mui/material";
import { renderTimeViewClock, TimePicker } from "@mui/x-date-pickers-pro";
import { Moment } from "moment";
import { FC, useEffect } from "react";
import { Controller, useForm } from "react-hook-form";

import { ICameraControlsData } from "../../../../interfaces";
import { useAddCameraProfileMutation } from "../../../../redux/reducers/configuration.reducer";
import "./addProfileDialog.css";

interface IAddProfileFormData {
	name: string;
	start_time: Moment | null;
}

interface IAddProfileDialogProps {
	open: boolean;
	cameraId: number;
	onClose: (profileDetails?: ICameraControlsData) => void;
}

const AddProfileDialog: FC<IAddProfileDialogProps> = (props) => {
	const { open, cameraId, onClose } = props;

	// ADD CAMERA PROFILE
	const [
		addCameraProfile,
		{ isLoading: addCameraProfileLoading, error: addCameraProfileError, reset: resetAddCameraProfileState }
	] = useAddCameraProfileMutation();

	const {
		control,
		handleSubmit,
		reset,
		formState: { errors }
	} = useForm<IAddProfileFormData>({
		defaultValues: {
			name: "",
			start_time: null
		}
	});

	const handleFormSubmit = (data: IAddProfileFormData) => {
		addCameraProfile({
			camera_id: cameraId,
			data: {
				name: data.name,
				start_time: data.start_time?.format("HH:mm:ss") || "",
				settings: {}
			}
		})
			.unwrap()
			.then((response) => onClose(response))
			.catch(() => {});
	};

	useEffect(() => {
		if (open) {
			reset();
		} else {
			resetAddCameraProfileState();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open]);

	return (
		<Dialog open={open} onClose={() => onClose()} fullWidth maxWidth="xs">
			<DialogTitle textAlign="center" color="white" classes={{ root: "add-profile-dialog-title-root" }}>
				Add New Profile
				<Box className="close-dialog-icon-wrapper">
					<IconButton color="inherit" onClick={() => onClose()}>
						<CloseIcon color="inherit" />
					</IconButton>
				</Box>
			</DialogTitle>

			<DialogContent classes={{ root: "add-profile-dialog-content-root" }}>
				<Box component="form" noValidate onSubmit={handleSubmit(handleFormSubmit)}>
					<Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
						{/* Profile Name */}
						<Controller
							name="name"
							control={control}
							rules={{ required: "Profile name is required" }}
							render={({ field }) => (
								<TextField
									{...field}
									fullWidth
									label="Profile Name"
									variant="outlined"
									size="small"
									error={!!errors.name}
									helperText={errors.name?.message}
								/>
							)}
						/>

						{/* Start Time */}
						<Controller
							name="start_time"
							control={control}
							rules={{ required: "Start time is required" }}
							render={({ field }) => (
								<TimePicker
									{...field}
									closeOnSelect={false}
									label="Start Time"
									viewRenderers={{ hours: renderTimeViewClock, minutes: renderTimeViewClock }}
									slotProps={{
										textField: {
											size: "small",
											fullWidth: true,
											error: !!errors.start_time,
											helperText: errors.start_time?.message
										}
									}}
								/>
							)}
						/>

						{addCameraProfileError ? (
							<Typography variant="body2" color="var(--color-error-main)" textAlign="center">
								{"data" in addCameraProfileError && addCameraProfileError.data
									? String(addCameraProfileError.data)
									: "Failed to add camera profile"}
							</Typography>
						) : null}
					</Box>

					<Box className="add-profile-dialog-actions-wrapper">
						<Button variant="outlined" color="error" onClick={() => onClose()}>
							Cancel
						</Button>

						<LoadingButton
							disableElevation
							variant="contained"
							color="primary"
							type="submit"
							loading={addCameraProfileLoading}
						>
							Add Profile
						</LoadingButton>
					</Box>
				</Box>
			</DialogContent>
		</Dialog>
	);
};

export default AddProfileDialog;
